import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import {
  buttonBaseClass,
  buttonSizeStyles,
  buttonToneStyles,
  type ButtonVariantType,
} from '../config/button';
import {
  type IconPositionType,
  type LegacyToneType,
  type SizeType,
  type ToneType,
  resolveLegacyTone,
} from '../config/types';
import { cn } from '../utils/cn';

interface ButtonProps extends Omit<ButtonPrimitive.Props, 'disabled' | 'color'> {
  tone?: ToneType;
  variant?: ButtonVariantType;
  size?: SizeType;
  icon?: ReactNode;
  iconPosition?: IconPositionType;
  isLoading?: boolean;
  isDisabled?: boolean;
  // Backward compatibility alias
  color?: LegacyToneType;
}

function Button({
  className,
  tone,
  color = 'primary',
  variant = 'solid',
  size = 'medium',
  icon,
  iconPosition = 'start',
  isLoading = false,
  isDisabled = false,
  children,
  ...props
}: ButtonProps) {
  const reduceMotion = useReducedMotion();
  const resolvedTone = resolveLegacyTone(tone, color);
  const resolvedIcon = isLoading ? <CircleNotchIcon size={14} className="animate-spin" /> : icon;
  const iconDataSlot = iconPosition === 'end' ? 'inline-end' : 'inline-start';
  const disabled = isDisabled || isLoading;

  return (
    <ButtonPrimitive
      render={
        <motion.button
          whileTap={disabled || reduceMotion ? undefined : { scale: 0.97 }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
        />
      }
      data-slot="button"
      className={cn(
        buttonBaseClass,
        buttonSizeStyles[size],
        buttonToneStyles[variant][resolvedTone],
        className,
      )}
      disabled={disabled}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {resolvedIcon && iconPosition === 'start' ? (
        <span data-icon={iconDataSlot} className="inline-flex items-center">
          {resolvedIcon}
        </span>
      ) : null}
      {children}
      {resolvedIcon && iconPosition === 'end' ? (
        <span data-icon={iconDataSlot} className="inline-flex items-center">
          {resolvedIcon}
        </span>
      ) : null}
    </ButtonPrimitive>
  );
}

export { Button };
export type { ButtonProps, ButtonVariantType };
